import React, { RefObject } from "react";
import {
  Hotel,
  SortHotelsBy,
  wsAvailability,
} from "../../../assets/Services/wsSearch";
import { currencySymbols } from "../../../assets/Utils/prices";

export function dateToSearchFormat(date: Date) {
  date = new Date(date);

  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");

  return `${y}-${m}-${d}`;
}

export function calculateAverageInArray(array: number[]) {
  // Check if the array is not empty
  if (array.length === 0) return 0;

  // Sum all the elements in the array
  const sum = array.reduce((acc, number) => acc + number, 0);

  // Calculate the average
  const average = sum / array.length;

  return average;
}

export function keepKeys<T extends Object>(
  object: T,
  keys: Array<keyof T>,
): Partial<T> {
  const result: Partial<T> = {};

  keys.forEach((key) => {
    if (key in object) {
      result[key] = object[key];
    }
  });

  return result;
}

export function removeKeys<T extends Object>(
  object: T,
  keys: Array<keyof T>,
): Partial<T> {
  const result: Partial<T> = { ...object };

  keys.forEach((key) => {
    if (key in result) {
      delete result[key];
    }
  });

  return result;
}

/**
 * A React component that renders parts of a translated text with styling and event handlers.
 *
 * @example
 * <Render
 *   text={t("loyalty.accept_terms")}
 *   props={{
 *     className: "font-medium text-takyon-600 hover:underline",
 *     onClick: () => window.open('https://takyon.io/terms', '_blank')
 *   }}
 * />
 */
export function Render({
  text,
  props,
}: {
  text: string;
  props: React.HTMLAttributes<HTMLSpanElement>;
}): JSX.Element {
  if (!text) return React.createElement("span", null);

  // Split the text by {{ and }}
  const parts = text.split(/{{|}}/);

  // Create elements array
  const elements: React.ReactNode[] = [];

  // Process each part
  parts.forEach((part, index) => {
    // Even indices are regular text, odd indices are highlighted parts
    if (index % 2 === 0) {
      if (part) elements.push(part);
    } else {
      // This is a highlighted part, apply the props
      elements.push(
        React.createElement(
          "span",
          { key: `highlight-${index}`, ...props },
          part,
        ),
      );
    }
  });

  return React.createElement(React.Fragment, null, ...elements);
}

export function ensureInView(ref: RefObject<HTMLDivElement>) {
  if (!ref.current) {
    return;
  }

  const rect = ref.current.getBoundingClientRect();

  const completelyVisible =
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth);

  if (!completelyVisible) {
    ref.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }
}

export const sortHotels = (
  hotels: Hotel[],
  hotel: Hotel,
  sorting: SortHotelsBy,
  searchedStructure?: string,
): Hotel[] => {
  const score = computeScore(hotel, sorting, searchedStructure);
  let position = hotels.length;

  for (let i = 0; i < hotels.length; i++) {
    const prev = hotels[i];
    const prevScore = computeScore(prev, sorting, searchedStructure);
    if (score > prevScore) {
      position = i;
      break;
    }
  }
  const firstHalf = hotels.slice(0, position);
  const secondHalf = hotels.slice(position, hotels.length);
  return [...firstHalf, hotel, ...secondHalf];
};

const computeScore = (
  hotel: Hotel,
  sorting: SortHotelsBy,
  searchedStructure?: string,
): number => {
  if (hotel.name === searchedStructure) {
    return 1000000;
  }

  switch (sorting) {
    case SortHotelsBy.RECOMMENDED:
      return (
        Number(hotel.isActive === true) * 2 +
        Number(
          hotel.isLoyaltyEnabledAdmin === true &&
            hotel.isLoyaltyEnabled === true,
        )
      );
    case SortHotelsBy.DISTANCE_TO_CC:
      return 1000000 - hotel.distanceToCc;
    case SortHotelsBy.REVIEWS:
      return hotel.reviewScore ?? 0;
    case SortHotelsBy.PRICE:
      return 1000000 - (hotel.availability?.availabilityInfo.value ?? 1000000);
    default:
      return 0;
  }
};

export function extractDomain(url?: string): string | null {
  try {
    if (!url) {
      return null;
    }
    const { hostname } = new URL(url);
    // strip "www." if present
    return hostname.replace(/^www\./, "");
  } catch {
    return null; // invalid URL
  }
}

export const getDisplayPrice = (payload: {
  availability: wsAvailability;
  deltaBooking?: number;
  deltaHotels?: number;
  lang?: string;
}) => {
  const { availability, deltaBooking = 0, deltaHotels = 0, lang } = payload;
  const cappedDeltaBooking = Math.min(0.2, deltaBooking);
  const cappedDeltaHotels = Math.min(0.2, deltaHotels);

  if (availability.availabilityInfo.provider === "bookingcom") {
    return `${new Intl.NumberFormat(lang ?? "en-US").format(
      Number(
        (
          availability.availabilityInfo.value *
          (1 - cappedDeltaBooking)
        ).toFixed(0),
      ),
    )}${currencySymbols[availability.availabilityInfo.currency]}`;
  }
  if (availability.availabilityInfo.provider === "hotelscom") {
    return `${new Intl.NumberFormat(lang ?? "en-US").format(
      Number(
        (availability.availabilityInfo.value * (1 - cappedDeltaHotels)).toFixed(
          0,
        ),
      ),
    )}${currencySymbols[availability.availabilityInfo.currency]}`;
  }
  return `${new Intl.NumberFormat(lang ?? "en-US").format(
    Number(availability.availabilityInfo.value.toFixed(0)),
  )}${currencySymbols[availability.availabilityInfo.currency]}`;
};

export const getOTADeltaPrice = (payload: {
  availability: wsAvailability;
  deltaBooking?: number;
  deltaHotels?: number;
  lang?: string;
}) => {
  const { availability, deltaBooking = 0, deltaHotels = 0, lang } = payload;
  const cappedDeltaBooking = Math.min(0.2, deltaBooking);
  const cappedDeltaHotels = Math.min(0.2, deltaHotels);
  if (
    availability.availabilityInfo.provider === "bookingcom" &&
    cappedDeltaBooking > 0
  ) {
    return `-${
      currencySymbols[availability.availabilityInfo.currency]
    }${new Intl.NumberFormat(lang ?? "en-US").format(
      Number(
        (availability.availabilityInfo.value * cappedDeltaBooking).toFixed(0),
      ),
    )} vs. Booking`;
  }
  if (
    availability.availabilityInfo.provider === "hotelscom" &&
    cappedDeltaHotels > 0
  ) {
    return `-${
      currencySymbols[availability.availabilityInfo.currency]
    }${new Intl.NumberFormat(lang ?? "en-US").format(
      Number(
        (availability.availabilityInfo.value * cappedDeltaHotels).toFixed(0),
      ),
    )} vs. Hotels.com`;
  }
  if (cappedDeltaBooking > 0) {
    return `-${
      currencySymbols[availability.availabilityInfo.currency]
    }${new Intl.NumberFormat(lang ?? "en-US").format(
      Number(
        (
          availability.availabilityInfo.value * (1 + cappedDeltaBooking) -
          availability.availabilityInfo.value
        ).toFixed(0),
      ),
    )} vs. Booking`;
  }
  if (cappedDeltaHotels > 0) {
    return `-${
      currencySymbols[availability.availabilityInfo.currency]
    }${new Intl.NumberFormat(lang ?? "en-US").format(
      Number(
        (
          availability.availabilityInfo.value * (1 + cappedDeltaHotels) -
          availability.availabilityInfo.value
        ).toFixed(0),
      ),
    )} vs. Hotels.com`;
  }
  return "";
};
